// Typed fetchers, one per endpoint the Phase 3 and 4 surfaces read. Each takes
// an optional AbortSignal so TanStack Query can cancel on unmount.

import { accessRequest, apiFetch, apiRequest, buildQuery } from './client';
import {
  API_PATHS,
  ARCHIVE_REQUEST_TIMEOUT_MS,
  AUTH_REQUEST_TIMEOUT_MS,
  SEARCH,
  coinCatalystDetailPath,
  coinCatalystFeedbackPath,
  marketOverviewPath,
  workspaceStatePath,
} from './config';
import type {
  AccessChallenge,
  AccessChallengePasswordResponse,
  AccessChallengeWorkspaceResponse,
  AlertsSummary,
  ArchiveCallFilters,
  ArchiveCallsResponse,
  AuthIdentity,
  DiscordStartResponse,
  EmailVerifyResponse,
  InvitationAcceptBody,
  InvitationAcceptResponse,
  LoginBody,
  LoginResponse,
  PasswordCompleteResponse,
  PasswordRequestResponse,
  CallTrackerCallersResponse,
  CallTrackerCallsResponse,
  CandleInterval,
  CoinSearchResponse,
  CoinCatalystsResponse,
  SuppressedCatalystDetailResponse,
  CoinCatalystRefreshResponse,
  CoinCatalystSettingsBody,
  CoinCatalystSettingsResponse,
  CatalystFeedbackBody,
  CatalystFeedbackResponse,
  ForwardHorizon,
  HealthReadyResponse,
  HealthReadySnapshot,
  MarketComparisonRange,
  MarketComparisonResponse,
  MarketHistoryJobBody,
  MarketHistoryJobResponse,
  MarketOverview,
  MarketRuntimeResponse,
  ResearchCacheResponse,
  ResearchRiskResponse,
  SearchEntitiesResponse,
  SourceConnectorsResponse,
  WorkspaceStateDocument,
  WorkspaceStateKind,
  WorkspaceStatePutBody,
  WorkspacesResponse,
} from './types';

export function getCoinCatalysts(signal?: AbortSignal): Promise<CoinCatalystsResponse> {
  return apiRequest<CoinCatalystsResponse>(API_PATHS.coinCatalysts, signal ? { signal } : {});
}

export function getSuppressedCoinCatalystDetail(
  eventId: string,
  signal?: AbortSignal,
): Promise<SuppressedCatalystDetailResponse> {
  return apiRequest<SuppressedCatalystDetailResponse>(
    coinCatalystDetailPath(eventId),
    signal ? { signal } : {},
  );
}

export function postCoinCatalystsRefresh(): Promise<CoinCatalystRefreshResponse> {
  return apiRequest<CoinCatalystRefreshResponse>(API_PATHS.coinCatalystsRefresh, {
    method: 'POST',
    body: {},
    mutation: 'session',
  });
}

export function putCoinCatalystSettings(
  body: CoinCatalystSettingsBody,
): Promise<CoinCatalystSettingsResponse> {
  return apiRequest<CoinCatalystSettingsResponse>(API_PATHS.coinCatalystsSettings, {
    method: 'PUT',
    body,
    mutation: 'session',
  });
}

export function patchCoinCatalystFeedback(
  eventId: string,
  body: CatalystFeedbackBody,
): Promise<CatalystFeedbackResponse> {
  return apiRequest<CatalystFeedbackResponse>(coinCatalystFeedbackPath(eventId), {
    method: 'PATCH',
    body,
    mutation: 'session',
  });
}

export function getAuthMe(signal?: AbortSignal): Promise<AuthIdentity> {
  return accessRequest<AuthIdentity>(API_PATHS.authMe, signal ? { signal } : {});
}

export function getWorkspaces(signal?: AbortSignal): Promise<WorkspacesResponse> {
  return apiRequest<WorkspacesResponse>(API_PATHS.workspaces, signal ? { signal } : {});
}

export interface ArchiveCallsPageRequest {
  readonly filters: ArchiveCallFilters;
  readonly cursor?: string;
  readonly signal?: AbortSignal;
}

export function getArchiveCalls(request: ArchiveCallsPageRequest): Promise<ArchiveCallsResponse> {
  const query = buildQuery({ ...request.filters, cursor: request.cursor });
  return apiRequest<ArchiveCallsResponse>(`${API_PATHS.archiveCalls}${query}`, {
    timeoutMs: ARCHIVE_REQUEST_TIMEOUT_MS,
    ...(request.signal ? { signal: request.signal } : {}),
  });
}

// Global search (api-surface 2.1). Entity matches: wallets, providers,
// groups, people, creators. Archive matches reuse the calls envelope, so the
// ambiguity block travels with them and the suggestions layer can refuse to
// guess between duplicate tickers.
export function getSearchEntities(
  q: string,
  limit: number = SEARCH.entityLimit,
  signal?: AbortSignal,
): Promise<SearchEntitiesResponse> {
  return apiRequest<SearchEntitiesResponse>(
    `${API_PATHS.searchEntities}${buildQuery({ q, limit })}`,
    {
      timeoutMs: SEARCH.timeoutMs,
      ...(signal ? { signal } : {}),
    },
  );
}

export function getArchiveSearch(
  q: string,
  limit: number = SEARCH.archiveLimit,
  signal?: AbortSignal,
): Promise<ArchiveCallsResponse> {
  return apiRequest<ArchiveCallsResponse>(`${API_PATHS.archiveSearch}${buildQuery({ q, limit })}`, {
    timeoutMs: SEARCH.timeoutMs,
    ...(signal ? { signal } : {}),
  });
}

export function getSourceConnectors(signal?: AbortSignal): Promise<SourceConnectorsResponse> {
  return apiRequest<SourceConnectorsResponse>(API_PATHS.sourceConnectors, signal ? { signal } : {});
}

// Anonymous. A 503 carries the same body shape and is returned as data.
export async function getHealthReady(signal?: AbortSignal): Promise<HealthReadySnapshot> {
  const response = await apiFetch<HealthReadyResponse>(API_PATHS.healthReady, {
    tolerateStatuses: [503],
    ...(signal ? { signal } : {}),
  });
  return { httpStatus: response.status, body: response.body };
}

export interface CallTrackerCallersRequest {
  readonly limit?: number;
  readonly horizon?: ForwardHorizon;
  readonly minCalls?: number;
  readonly signal?: AbortSignal;
}

export function getCallTrackerCallers(
  request: CallTrackerCallersRequest = {},
): Promise<CallTrackerCallersResponse> {
  const query = buildQuery({
    limit: request.limit,
    horizon: request.horizon,
    minCalls: request.minCalls,
  });
  return apiRequest<CallTrackerCallersResponse>(
    `${API_PATHS.callTrackerCallers}${query}`,
    request.signal ? { signal: request.signal } : {},
  );
}

export interface CallTrackerCallsRequest {
  readonly limit?: number;
  readonly signal?: AbortSignal;
}

export function getCallTrackerCalls(
  request: CallTrackerCallsRequest = {},
): Promise<CallTrackerCallsResponse> {
  const query = buildQuery({ limit: request.limit });
  return apiRequest<CallTrackerCallsResponse>(
    `${API_PATHS.callTrackerCalls}${query}`,
    request.signal ? { signal: request.signal } : {},
  );
}

export function getMarketOverview(
  network: string,
  contract: string,
  signal?: AbortSignal,
): Promise<MarketOverview> {
  return apiRequest<MarketOverview>(
    marketOverviewPath(network, contract),
    signal ? { signal } : {},
  );
}

export function getMarketRuntime(signal?: AbortSignal): Promise<MarketRuntimeResponse> {
  return apiRequest<MarketRuntimeResponse>(API_PATHS.marketRuntime, signal ? { signal } : {});
}

export function getMarketComparison(
  range: MarketComparisonRange,
  signal?: AbortSignal,
): Promise<MarketComparisonResponse> {
  return apiRequest<MarketComparisonResponse>(
    `${API_PATHS.marketComparison}${buildQuery({ range })}`,
    signal ? { signal } : {},
  );
}

// Gap-fill for a stale chart (useCandleTopUp): queues a bounded candle-history
// job for one retained call. A viewer role or a job that already exists
// answers 403 or 409; both are expected outcomes the caller swallows.
export function postMarketHistoryJob(
  body: MarketHistoryJobBody,
): Promise<MarketHistoryJobResponse> {
  return apiRequest<MarketHistoryJobResponse>(API_PATHS.marketHistoryJobs, {
    method: 'POST',
    body,
    mutation: 'session',
  });
}

export function getResearchCache(
  address: string,
  interval?: CandleInterval,
  signal?: AbortSignal,
): Promise<ResearchCacheResponse> {
  const query = buildQuery({ address, interval });
  return apiRequest<ResearchCacheResponse>(
    `${API_PATHS.researchCache}${query}`,
    signal ? { signal } : {},
  );
}

export function getResearchRisk(
  address: string,
  chain?: string,
  signal?: AbortSignal,
): Promise<ResearchRiskResponse> {
  const query = buildQuery({ address, chain });
  return apiRequest<ResearchRiskResponse>(
    `${API_PATHS.researchRisk}${query}`,
    signal ? { signal } : {},
  );
}

export function getCoinSearch(
  q: string,
  network?: string,
  signal?: AbortSignal,
): Promise<CoinSearchResponse> {
  const query = buildQuery({ q, network });
  return apiRequest<CoinSearchResponse>(
    `${API_PATHS.coinSearch}${query}`,
    signal ? { signal } : {},
  );
}

export function getWorkspaceState<TPayload = Record<string, unknown>>(
  kind: WorkspaceStateKind,
  signal?: AbortSignal,
): Promise<WorkspaceStateDocument<TPayload>> {
  return apiRequest<WorkspaceStateDocument<TPayload>>(
    workspaceStatePath(kind),
    signal ? { signal } : {},
  );
}

export function putWorkspaceState<TPayload = Record<string, unknown>>(
  kind: WorkspaceStateKind,
  body: WorkspaceStatePutBody<TPayload>,
): Promise<WorkspaceStateDocument<TPayload>> {
  return apiRequest<WorkspaceStateDocument<TPayload>>(workspaceStatePath(kind), {
    method: 'PUT',
    body,
    mutation: 'session',
  });
}

export function getAlertsSummary(signal?: AbortSignal): Promise<AlertsSummary> {
  return apiRequest<AlertsSummary>(API_PATHS.alertsSummary, signal ? { signal } : {});
}

// Auth mutations (api-surface 2.0). Anonymous routes check Origin only, so no
// CSRF scope; logout carries the session CSRF token. Each aborts at 15 s.
const AUTH_TIMEOUT = { timeoutMs: AUTH_REQUEST_TIMEOUT_MS } as const;

export function postDiscordStart(): Promise<DiscordStartResponse> {
  return apiRequest<DiscordStartResponse>(API_PATHS.discordStart, {
    method: 'POST',
    body: {},
    ...AUTH_TIMEOUT,
  });
}

export function postLogin(body: LoginBody): Promise<LoginResponse> {
  return apiRequest<LoginResponse>(API_PATHS.login, { method: 'POST', body, ...AUTH_TIMEOUT });
}

// Workspace switch (api-surface 2.0). The answer is the new identity, so the
// caller invalidates auth/me and the shell re-renders with the new tenant.
export function postWorkspaceSwitch(tenantId: string): Promise<AuthIdentity> {
  return apiRequest<AuthIdentity>(API_PATHS.workspaceSwitch, {
    body: { tenantId },
    mutation: 'session',
    timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
  });
}

export function postLogout(): Promise<void> {
  return apiRequest<void>(API_PATHS.logout, {
    method: 'POST',
    body: {},
    mutation: 'session',
    ...AUTH_TIMEOUT,
  });
}

export function postInvitationAccept(
  body: InvitationAcceptBody,
): Promise<InvitationAcceptResponse> {
  return apiRequest<InvitationAcceptResponse>(API_PATHS.invitationAccept, {
    method: 'POST',
    body,
    ...AUTH_TIMEOUT,
  });
}

export function postEmailVerify(token: string): Promise<EmailVerifyResponse> {
  return apiRequest<EmailVerifyResponse>(API_PATHS.emailVerify, {
    method: 'POST',
    body: { token },
    ...AUTH_TIMEOUT,
  });
}

export function postEmailResend(email: string): Promise<void> {
  return apiRequest<void>(API_PATHS.emailResend, {
    method: 'POST',
    body: { email },
    ...AUTH_TIMEOUT,
  });
}

// Body shape {email} is the desk's; api-surface 2.0 lists only the answer.
export function postPasswordRequest(email: string): Promise<PasswordRequestResponse> {
  return apiRequest<PasswordRequestResponse>(API_PATHS.passwordRequest, {
    method: 'POST',
    body: { email },
    ...AUTH_TIMEOUT,
  });
}

export function postPasswordComplete(
  token: string,
  password: string,
): Promise<PasswordCompleteResponse> {
  return apiRequest<PasswordCompleteResponse>(API_PATHS.passwordComplete, {
    method: 'POST',
    body: { token, password },
    ...AUTH_TIMEOUT,
  });
}

// Access challenge (api-surface 1.4 and 2.0). The challenge cookie is set by
// the 409 or 428 login answer; these routes carry the aleph_access_csrf token
// instead of the session one, which is what mutation: 'access' selects.
export function getAccessChallenge(signal?: AbortSignal): Promise<AccessChallenge> {
  return apiRequest<AccessChallenge>(API_PATHS.accessChallenge, {
    timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
    ...(signal ? { signal } : {}),
  });
}

export function postAccessChallengeWorkspace(
  tenantId: string,
): Promise<AccessChallengeWorkspaceResponse> {
  return apiRequest<AccessChallengeWorkspaceResponse>(API_PATHS.accessChallengeWorkspace, {
    body: { tenantId },
    mutation: 'access',
    timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
  });
}

export function postAccessChallengePassword(
  password: string,
): Promise<AccessChallengePasswordResponse> {
  return apiRequest<AccessChallengePasswordResponse>(API_PATHS.accessChallengePassword, {
    body: { password },
    mutation: 'access',
    timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
  });
}

export function postAccessChallengeCancel(): Promise<void> {
  return apiRequest<void>(API_PATHS.accessChallengeCancel, {
    method: 'POST',
    mutation: 'access',
    timeoutMs: AUTH_REQUEST_TIMEOUT_MS,
  });
}
